import type { OrganizationSpeciesOption } from "@/api/species/OrganizationSpeciesApi";
import { useTheme } from "@/theme/ThemeProvider";
import { focusTarget } from "@/ui/a11y/focusTarget";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import {
  SPECIES_FREE_TEXT_AT_MAX,
  SPECIES_FREE_TEXT_HINT,
  SPECIES_FREE_TEXT_MAX,
  SPECIES_FREE_TEXT_TRUNCATED,
  INTAKE_UNKNOWN_SPECIES_CODE,
  INTAKE_UNKNOWN_SPECIES_ID,
  SPECIES_LABEL,
  SPECIES_LIST_UNAVAILABLE,
  SPECIES_REQUIRED,
  SPECIES_REQUIRED_ALERT_ID,
} from "@/ui/intake/intakeCopy";
import {
  INTAKE_ERROR_BAND,
  INTAKE_ERROR_OUTLINE_OFFSET,
  intakeChipBorderColor,
} from "@/ui/intake/intakeChipChrome";
import { focusSpeciesControl } from "@/ui/intake/intakeFocus";
import { ensureIntakeUnknownSpecies } from "@/ui/intake/intakeSpecies";
import { useCoarseTarget } from "@/ui/intake/useCoarseTarget";
import { useEffect, useRef, useState, type RefObject } from "react";
import { findNodeHandle, Platform, Pressable, ScrollView, Text, TextInput, View } from "react-native";

type KeyEvent = {
  key?: string;
  repeat?: boolean;
  nativeEvent?: { key?: string; repeat?: boolean };
  preventDefault?: () => void;
};

const CHIP_FOCUS_STYLE_ID = "ferina-intake-chip-focus";

/**
 * react-native-web zeichnet keinen eigenen Ring. Der Browser-Ring wird auf
 * :focus aus- und auf :focus-visible mit dem Token neu gesetzt, damit ein
 * Mausklick keinen zweiten, dauerhaften Ring hinterlässt.
 * :focus-visible selbst gibt es ab Chrome 86, Safari 15.4 und Firefox 85.
 * Das Erzwingen über focus({ focusVisible }) ist jünger, siehe focusTarget.
 */
function useWebChipFocusRing(color: string) {
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    let style = document.getElementById(CHIP_FOCUS_STYLE_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = CHIP_FOCUS_STYLE_ID;
      document.head.appendChild(style);
    }
    style.textContent = `
      [data-intake-chip="1"]:focus {
        outline: none;
      }
      [data-intake-chip="1"]:focus-visible {
        outline: 3px solid ${color};
        outline-offset: 2px;
      }
    `;
  }, [color]);
}

type Props = {
  options: OrganizationSpeciesOption[];
  speciesId: string | null;
  freeText: string;
  onChangeSpeciesId: (id: string | null) => void;
  onChangeFreeText: (text: string) => void;
  onFreeTextSubmit?: () => void;
  listError?: boolean;
  /** Nach einem Speicherversuch ohne Art. Der Hinweis ist vorher schon sichtbar. */
  requiredError?: boolean;
  /** Steigt nach einem fehlgeschlagenen Speichern. 0 lässt den Fokus unangetastet. */
  focusRequest?: number;
  /** Native: die Aufnahme-Liste, in die das Feld nach dem Versuch scrollt. */
  scrollRef?: RefObject<ScrollView | null>;
};

export function IntakeSpeciesField({
  options,
  speciesId,
  freeText,
  onChangeSpeciesId,
  onChangeFreeText,
  onFreeTextSubmit,
  listError = false,
  requiredError = false,
  focusRequest = 0,
  scrollRef,
}: Props) {
  const { theme } = useTheme();
  const coarse = useCoarseTarget();
  const chipMin = coarse ? theme.control.heightTouch : theme.control.heightDense;
  const fieldRef = useRef<View>(null);
  const chipRefs = useRef<(View | null)[]>([]);
  const freeTextRef = useRef<TextInput>(null);
  const [focusedChip, setFocusedChip] = useState<number | null>(null);
  const [freeTextCut, setFreeTextCut] = useState(false);
  const choices = ensureIntakeUnknownSpecies(options);
  useWebChipFocusRing(theme.color.text);
  const errorStyle =
    Platform.OS === "web"
      ? requiredError
        ? {
            outlineWidth: 2,
            outlineStyle: "solid" as const,
            outlineColor: theme.color.dangerText,
            outlineOffset: INTAKE_ERROR_OUTLINE_OFFSET,
          }
        : null
      : {
          // Band bleibt stehen: Fehler tauscht Padding gegen Rahmen, ohne zu springen.
          borderWidth: requiredError ? INTAKE_ERROR_BAND : 0,
          padding: requiredError ? 0 : INTAKE_ERROR_BAND,
          borderColor: requiredError ? theme.color.dangerText : "transparent",
          borderRadius: theme.radius.control,
        };

  useEffect(() => {
    if (!focusRequest) return;
    const field = fieldRef.current;
    const parent = scrollRef?.current ?? null;
    let relative: number | null = null;
    if (Platform.OS !== "web" && parent) {
      try {
        const handle = findNodeHandle(parent);
        relative = typeof handle === "number" ? handle : null;
      } catch {
        relative = null;
      }
    }
    const landed = focusSpeciesControl(
      chipRefs.current[0],
      freeTextRef.current,
      field,
      parent,
      typeof relative === "number" ? relative : null,
    );
    if (landed === "chip") setFocusedChip(0);
  }, [focusRequest, scrollRef]);

  useEffect(() => {
    const catalogUnknown = options.find((option) => option.code === INTAKE_UNKNOWN_SPECIES_CODE);
    if (!catalogUnknown || speciesId !== INTAKE_UNKNOWN_SPECIES_ID) return;
    onChangeSpeciesId(catalogUnknown.id);
  }, [options, speciesId, onChangeSpeciesId]);

  const selectIndex = (index: number, fromKeyboard: boolean) => {
    const option = choices[index];
    if (!option) return;
    const selected = speciesId === option.id && freeText.trim().length === 0;
    setFreeTextCut(false);
    if (selected) onChangeSpeciesId(null);
    else {
      onChangeFreeText("");
      onChangeSpeciesId(option.id);
    }
    // Klick ohne focusVisible, sonst bleibt in Firefox der Ring stehen.
    focusTarget(chipRefs.current[index], fromKeyboard);
  };

  const onChipKey = (index: number, event: KeyEvent) => {
    if (event.repeat ?? event.nativeEvent?.repeat) return;
    const key = event.key ?? event.nativeEvent?.key;
    if (key === " " || key === "Spacebar") {
      event.preventDefault?.();
      selectIndex(index, true);
      return;
    }
    if (key !== "ArrowRight" && key !== "ArrowDown" && key !== "ArrowLeft" && key !== "ArrowUp") {
      return;
    }
    event.preventDefault?.();
    const delta = key === "ArrowLeft" || key === "ArrowUp" ? -1 : 1;
    const next = (index + delta + choices.length) % choices.length;
    const option = choices[next];
    if (!option) return;
    setFreeTextCut(false);
    onChangeFreeText("");
    onChangeSpeciesId(option.id);
    focusTarget(chipRefs.current[next], true);
  };

  return (
    <View ref={fieldRef} testID="intake-species-field" style={{ gap: theme.space.dense }}>
      <Text
        style={{
          color: theme.color.textSecondary,
          fontSize: theme.fontSize.label,
          fontWeight: "600",
        }}
      >
        {SPECIES_LABEL}
        <Text
          style={{ color: theme.color.dangerText }}
          aria-hidden
          accessibilityElementsHidden
          importantForAccessibility="no"
        >
          {" *"}
        </Text>
      </Text>
      {listError ? (
        <Text
          accessibilityLiveRegion="polite"
          style={{ color: theme.color.warningText, fontSize: theme.fontSize.small }}
        >
          {SPECIES_LIST_UNAVAILABLE}
        </Text>
      ) : null}
      <Text
        key={requiredError ? `intake-species-alert-${focusRequest}` : "intake-species-hint"}
        nativeID={SPECIES_REQUIRED_ALERT_ID}
        {...(requiredError
          ? { accessibilityRole: "alert" as const, accessibilityLiveRegion: "assertive" as const }
          : {})}
        style={{
          color: requiredError ? theme.color.dangerText : theme.color.textSecondary,
          fontSize: theme.fontSize.small,
        }}
      >
        {requiredError ? (
          <Text aria-hidden accessibilityElementsHidden importantForAccessibility="no">
            {"! "}
          </Text>
        ) : null}
        {SPECIES_REQUIRED}
      </Text>
      {choices.length > 0 ? (
        <View
          accessibilityRole="radiogroup"
          accessibilityLabel={SPECIES_LABEL}
          {...{
            "aria-required": true as const,
            "aria-invalid": requiredError,
            "aria-describedby": SPECIES_REQUIRED_ALERT_ID,
          }}
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "flex-start",
            gap: theme.space.sm,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            ...errorStyle,
          }}
        >
          {choices.map((option, index) => {
            const selected = speciesId === option.id && freeText.trim().length === 0;
            const selectedIndex = choices.findIndex(
              (item) => item.id === speciesId && freeText.trim().length === 0,
            );
            const tabStop = selectedIndex >= 0 ? selectedIndex : 0;
            return (
              <Pressable
                key={option.id}
                ref={(node) => {
                  chipRefs.current[index] = node;
                }}
                accessibilityRole="radio"
                accessibilityLabel={option.name}
                accessibilityState={{ checked: selected }}
                {...(Platform.OS === "web"
                  ? {
                      "aria-checked": selected,
                      tabIndex: index === tabStop ? 0 : -1,
                      dataSet: { intakeChip: "1" },
                      onKeyDown: (event: KeyEvent) => onChipKey(index, event),
                    }
                  : {})}
                onPress={() => selectIndex(index, false)}
                onFocus={() => {
                  if (Platform.OS !== "web") setFocusedChip(index);
                }}
                onBlur={() => {
                  if (Platform.OS !== "web") {
                    setFocusedChip((current) => (current === index ? null : current));
                  }
                }}
                style={{
                  minHeight: chipMin,
                  minWidth: chipMin,
                  maxWidth: "100%",
                  flexShrink: 1,
                  paddingHorizontal: theme.space.sm,
                  borderRadius: theme.radius.control,
                  borderWidth: 2,
                  borderColor:
                    Platform.OS === "web"
                      ? selected
                        ? theme.color.primary
                        : theme.color.inputBorder
                      : intakeChipBorderColor(focusedChip === index, selected, theme.color),
                  backgroundColor: selected ? theme.color.accentBg : theme.color.surface,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    color: theme.color.text,
                    fontSize: theme.fontSize.small,
                    fontWeight: selected ? "700" : "500",
                  }}
                >
                  <Text
                    style={{ opacity: selected ? 1 : 0, fontWeight: "700" }}
                    aria-hidden
                    accessibilityElementsHidden
                    importantForAccessibility="no"
                  >
                    {"✓ "}
                  </Text>
                  {option.name}
                </Text>
              </Pressable>
            );
          })}
        </View>
      ) : null}
      <View testID="intake-species-free-text" style={errorStyle ?? undefined}>
        <FloatingLabelInput
          ref={freeTextRef}
          label="Andere Art"
          value={freeText}
          onChangeText={(text) => {
            // maxLength würde den Paste vorher abschneiden, der Hinweis käme nicht an.
            const cut = text.length > SPECIES_FREE_TEXT_MAX;
            const next = cut ? text.slice(0, SPECIES_FREE_TEXT_MAX) : text;
            setFreeTextCut(cut);
            onChangeFreeText(next);
            if (next.trim().length > 0) onChangeSpeciesId(null);
          }}
          hint={SPECIES_FREE_TEXT_HINT}
          aria-describedby={SPECIES_REQUIRED_ALERT_ID}
          aria-invalid={requiredError}
          returnKeyType="next"
          blurOnSubmit={false}
          onSubmitEditing={onFreeTextSubmit}
        />
      </View>
      {freeTextCut ? (
        <Text
          accessibilityLiveRegion="polite"
          style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}
        >
          {SPECIES_FREE_TEXT_TRUNCATED}
        </Text>
      ) : freeText.length === SPECIES_FREE_TEXT_MAX ? (
        <Text
          accessibilityLiveRegion="polite"
          style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}
        >
          {SPECIES_FREE_TEXT_AT_MAX}
        </Text>
      ) : null}
    </View>
  );
}
